import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from "react";
import { updateConsent as updateGTMConsent } from "@/lib/gtm";

export interface ConsentState {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
  timestamp: string | null;
}

interface ConsentContextValue {
  consent: ConsentState | null;
  bannerVisible: boolean;
  acceptAll: () => void;
  acceptEssential: () => void;
  updateCategories: (analytics: boolean, marketing: boolean) => void;
  reopenBanner: () => void;
}

const STORAGE_KEY = "cookie_consent";
const ConsentContext = createContext<ConsentContextValue | null>(null);

function getStoredConsent(): ConsentState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as ConsentState;
  } catch {
    return null;
  }
}

export function ConsentProvider({ children }: { children: ReactNode }) {
  const [consent, setConsent] = useState<ConsentState | null>(() => getStoredConsent());
  const [bannerVisible, setBannerVisible] = useState(false);

  useEffect(() => {
    const stored = getStoredConsent();
    if (!stored) {
      setBannerVisible(true);
    } else {
      setConsent(stored);
      // Restore consent state to Google on page load
      updateGTMConsent(stored.analytics, stored.marketing);
    }
  }, []);

  const saveConsent = useCallback((state: ConsentState) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    setConsent(state);
    setBannerVisible(false);
    updateGTMConsent(state.analytics, state.marketing);
  }, []);

  const acceptAll = useCallback(() => {
    saveConsent({ essential: true, analytics: true, marketing: true, timestamp: new Date().toISOString() });
  }, [saveConsent]);

  const acceptEssential = useCallback(() => {
    saveConsent({ essential: true, analytics: false, marketing: false, timestamp: new Date().toISOString() });
  }, [saveConsent]);

  const updateCategories = useCallback((analytics: boolean, marketing: boolean) => {
    saveConsent({ essential: true, analytics, marketing, timestamp: new Date().toISOString() });
  }, [saveConsent]);

  const reopenBanner = useCallback(() => {
    setBannerVisible(true);
  }, []);

  return (
    <ConsentContext.Provider value={{ consent, bannerVisible, acceptAll, acceptEssential, updateCategories, reopenBanner }}>
      {children}
    </ConsentContext.Provider>
  );
}

export function useConsent() {
  const ctx = useContext(ConsentContext);
  if (!ctx) throw new Error("useConsent must be used within ConsentProvider");
  return ctx;
}
